import React, { useState, useEffect, useCallback } from 'react';
import TopMenu from '../../contexts/TopMenu';
import '../../styles/Fulfillment.css';
import { authService } from '../../services/auth';
import { API_BASE_URL } from '../../config';

function Returns({ email, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [returnOrders, setReturnOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [isReturnConfirmModalOpen, setIsReturnConfirmModalOpen] = useState(false);
  const [isReturnTrackingModalOpen, setIsReturnTrackingModalOpen] = useState(false);
  const [apiError, setApiError] = useState('');
  const [selectedTrackingInfo, setSelectedTrackingInfo] = useState(null);
  const [returnTrackingInfo, setReturnTrackingInfo] = useState(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [isReturnTrackingLoading, setIsReturnTrackingLoading] = useState(false);
  const [selectedItemForTracking, setSelectedItemForTracking] = useState(null);
  const [isDocumentLoading, setIsDocumentLoading] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [modalExpandedItems, setModalExpandedItems] = useState({});
  const [returnLocations, setReturnLocations] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);


  // Function to fetch return orders
  const fetchReturnOrders = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }
      
      const response = await fetch(`${API_BASE_URL}/brands/returns?page=${currentPage}&limit=${itemsPerPage}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch return orders');
      }
      
      setReturnOrders(data.data || []);
      
      // Set pagination information
      if (data.pagination) {
        setTotalPages(data.pagination.total_pages || 1);
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [currentPage, itemsPerPage]);

  // New function to update tracking information
  const updateTrackingInfo = async (orderId) => {
    try {
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        console.error('Authentication credentials not found');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/brands/orders/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId,
          order_id: orderId
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        console.error('Failed to update tracking info:', data.message);
        return;
      }
      
      console.log('Tracking info updated:', data);
      
      // Refresh orders list without showing loading screen
      await fetchOrders(false);
    } catch (error) {
      console.error('Error updating tracking info:', error);
    }
  };

  // Function to fetch return tracking information
  const fetchReturnTrackingInfo = async (orderId, returnId) => {
    try {
      setIsReturnTrackingLoading(true);
      setApiError('');
      
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }
      
      const response = await fetch(`${API_BASE_URL}/brands/returns/${orderId}/${returnId}/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch return tracking information');
      }
      
      if (!data.data) {
        throw new Error('No tracking data received from server');
      }
      
      // First set the tracking info data
      setReturnTrackingInfo(data.data);
      
      // Then open the modal
      setIsReturnTrackingModalOpen(true);
      
    } catch (error) {
      console.error('Error fetching return tracking info:', error);
      setApiError(error.message || 'Failed to fetch return tracking information');
      alert('Error fetching tracking info: ' + error.message);
    } finally {
      setIsReturnTrackingLoading(false);
    }
  };

  const fetchWarehouses = useCallback(async () => {
    try {
      // Get auth credentials from localStorage
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      // First try to fetch using POST with auth credentials
      if (authKey && userId) {
        try {
          const postResponse = await fetch(`${API_BASE_URL}/brands/warehouses`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              auth_key: authKey,
              user_id: userId
            })
          });
          
          const postResult = await postResponse.json();
          
          if (postResponse.ok && Array.isArray(postResult.data)) {
            console.log('Fetched warehouses:', postResult.data);
            return;
          }
        } catch (postError) {
          console.error('POST request failed, trying GET:', postError);
        }
      }
      
      // Fall back to GET with email parameter
      if (email) {
        const getResponse = await fetch(`${API_BASE_URL}/brands/warehouses?email=${encodeURIComponent(email)}`);
        const getResult = await getResponse.json();
        
        if (getResponse.ok && Array.isArray(getResult.data)) {
          console.log('Fetched warehouses (GET fallback):', getResult.data);
        } else {
          console.error('Failed to fetch warehouses with GET method');
        }
      }
    } catch (err) {
      console.error('Failed to fetch warehouses:', err);
    }
  }, [email]);

  useEffect(() => {
    fetchOrders();
    fetchReturnOrders();
    fetchWarehouses();
  }, [fetchWarehouses, fetchReturnOrders, currentPage]);

  const fetchOrders = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await authService.getOrders();
      setOrders(response.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleScheduleReturnPickup = (order) => {
    setSelectedOrder(order);
    setSelectedWarehouse(null);
    fetchReturnLocations();
    setIsReturnConfirmModalOpen(true);
  };

  const handleConfirmReturnPickup = async () => {
    try {
      setApiError('');
      setIsConfirmLoading(true);
      // Get auth credentials from localStorage
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }
      
      // Use returnId from the selected order's return details
      const returnId = selectedOrder.items[0]?.return_details?.return_id;
      
      if (!returnId) {
        throw new Error('Return ID not found');
      }

      // Validate warehouse selection
      if (!selectedWarehouse) {
        throw new Error('Please select a warehouse for return pickup');
      }
      
      const response = await fetch(`${API_BASE_URL}/brands/returns/${returnId}/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId,
          warehouse_id: selectedWarehouse.warehouse_id,
          order_id: selectedOrder.order_id
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to schedule return pickup');
      }

      // Refresh return orders list without showing loading screen
      await fetchReturnOrders(false);
      setIsReturnConfirmModalOpen(false);
    } catch (error) {
      console.error('Error scheduling return pickup:', error);
      setApiError(error.message || 'Failed to schedule return pickup');
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const handleViewTrackingInfo = (order, e) => {
    e.stopPropagation();
    
    // Update tracking info when View Tracking is clicked
    updateTrackingInfo(order.order_id);
    
    // Instead of finding just one item, we'll pass the entire order
    // so the user can select which item to track
    setSelectedOrder(order);
    setSelectedItemForTracking(null); // Reset selected item
    setIsTrackingModalOpen(true);
  };

  const selectItemForTracking = (item) => {
    if (item && item.shipping_info) {
      setSelectedTrackingInfo(item.shipping_info);
      setSelectedItemForTracking(item);
    }
  };

  const handleViewReturnTrackingInfo = (order, e) => {
    e.stopPropagation();
    
    // Set the selected order explicitly
    setSelectedOrder(order);
    
    // Get the return ID from the order
    const returnId = order.items[0]?.return_details?.return_id;
    if (!returnId) {
      console.error('Return ID not found for tracking');
      return;
    }
    
    // Fetch return tracking information
    fetchReturnTrackingInfo(order.order_id, returnId);
  };

  const checkForShippingInfo = (order) => {
    return order.items?.some(item => item.shipping_info);
  };

  const getDeliveredItemsCount = (order) => {
    return order.items?.filter(item => item.delivered_details)?.length || 0;
  };

  const handleConfirmSchedule = async () => {
    try {
      setApiError('');
      setIsConfirmLoading(true);
      // Get auth credentials from localStorage
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }
      
      const response = await fetch(`${API_BASE_URL}/brands/orders/ship`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId,
          order_id: selectedOrder.order_id,
          process_orders: true
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to schedule pickup');
      }

      // Refresh orders list without showing loading screen
      await fetchOrders(false);
      setIsConfirmModalOpen(false);
    } catch (error) {
      console.error('Error scheduling pickup:', error);
      setApiError(error.message || 'Failed to schedule pickup');
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
    
    // Update tracking info when order modal is opened
    updateTrackingInfo(order.order_id);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to fetch shipping documents
  const fetchShippingDocuments = async (orderId, shiprocketOrderId, shiprocketShipmentId) => {
    try {
      setIsDocumentLoading(prev => ({ ...prev, [shiprocketShipmentId]: true }));
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        console.error('Authentication credentials not found');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/brands/orders/${orderId}/label/${shiprocketOrderId}/${shiprocketShipmentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error('Failed to fetch shipping documents');
      }

      // Update the order to include shipping_documents
      const updatedOrders = orders.map(order => {
        if (order.order_id === orderId) {
          return {
            ...order,
            items: order.items.map(item => {
              if (item.shipping_info?.shiprocket_shipment_id === shiprocketShipmentId) {
                return {
                  ...item,
                  shipping_info: {
                    ...item.shipping_info,
                    shipping_documents: {
                      manifest_url: data.data.manifest_url,
                      label_url: data.data.label_url,
                      invoice_url: data.data.invoice_url
                    }
                  }
                };
              }
              return item;
            })
          };
        }
        return order;
      });
      setOrders(updatedOrders);

      // Update tracking information to refresh the data
      await updateTrackingInfo(orderId);
      
    } catch (error) {
      console.error('Error fetching shipping documents:', error);
      setApiError('Failed to fetch shipping documents');
    } finally {
      setIsDocumentLoading(prev => ({ ...prev, [shiprocketShipmentId]: false }));
    }
  };

  // Helper function to download document
  const downloadDocument = (url, fileName) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Function to toggle expanded item view in the modal
  const toggleModalExpandItem = (itemIndex) => {
    setModalExpandedItems(prev => ({
      ...prev,
      [itemIndex]: !prev[itemIndex]
    }));
  };

  // New function to fetch return locations
  const fetchReturnLocations = async () => {
    try {
      setIsLoadingLocations(true);
      setApiError('');
      
      // Get auth credentials from localStorage
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }
      
      const response = await fetch(`${API_BASE_URL}/brands/returns/locations/beeglee`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch return locations');
      }
      
      setReturnLocations(data.data || []);
      
      // Select the first warehouse by default if available
      if (data.data && data.data.length > 0) {
        setSelectedWarehouse(data.data[0]);
      }
      
    } catch (error) {
      console.error('Error fetching return locations:', error);
      setApiError(error.message || 'Failed to fetch return locations');
    } finally {
      setIsLoadingLocations(false);
    }
  };

  if (loading) {
    return (
      <div className="fulfillment-loading">
        <div className="loader"></div>
        <p>Loading orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fulfillment-error">
        <p>{error}</p>
        <button onClick={fetchOrders}>Retry</button>
      </div>
    );
  }

  return (
    <div className="dashboard-root">
      <TopMenu pageTitle="Returns Management" email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="fulfillment">
          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Total</th>
                  <th>Tracking</th>
                  <th>Return Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {returnOrders
                  .filter(order => order && order.shipping_address)
                  .map((order) => (
                    <tr key={order._id} onClick={() => handleOrderClick(order)}>
                      <td>{order.order_id}</td>
                      <td>{formatDate(order.created_at)}</td>
                      <td>{order.shipping_address.name ? `${order.shipping_address.name}` : 'N/A'}</td>
                      <td>{`${order.order_summary.total}`}</td>
                      <td>
                        {order.tracking_info && order.tracking_info.length > 0 ? (
                          <div className="tracking-info">
                            {order.tracking_info.map((tracking, index) => (
                              <a
                                key={index}
                                href={tracking.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  textDecoration: 'none',
                                  color: '#000',
                                  fontFamily: 'Helvetica, Arial, sans-serif',
                                  fontWeight: '300',
                                  fontSize: '13px',
                                  borderBottom: '1px solid #000',
                                  padding: '0 0 2px 0',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                {tracking.number}
                              </a>
                            ))}
                          </div>
                        ) : checkForShippingInfo(order) ? (
                          <button
                            className="view-tracking-btn"
                            onClick={(e) => handleViewTrackingInfo(order, e)}
                            style={{
                              padding: '8px 15px',
                              background: '#fff',
                              color: '#000',
                              border: '1px solid #000',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontFamily: 'Helvetica, Arial, sans-serif',
                              letterSpacing: '1px',
                              textTransform: 'uppercase',
                              fontWeight: '400',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {getDeliveredItemsCount(order) > 0 
                              ? `${getDeliveredItemsCount(order)} ${getDeliveredItemsCount(order) === 1 ? 'Item' : 'Items'} Delivered` 
                              : 'View Tracking'}
                          </button>
                        ) : (
                          <span className="no-tracking" style={{
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontSize: '13px',
                            color: '#666',
                            fontWeight: '300'
                          }}>
                            No tracking
                          </span>
                        )}
                      </td>
                      <td>
                        {order.items && order.items[0]?.return_details && (
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            backgroundColor: order.items[0].return_details.status === 'pending' ? '#ffecb3' : 
                                           order.items[0].return_details.status === 'approved' ? '#c8e6c9' :
                                           order.items[0].return_details.status === 'rejected' ? '#ffcdd2' : 
                                           order.items[0].return_details.status === 'processing' ? '#e1f5fe' :
                                           order.items[0].return_details.status === 'shipping_created' ? '#bbdefb' : '#e0e0e0',
                            color: order.items[0].return_details.status === 'pending' ? '#f57c00' : 
                                   order.items[0].return_details.status === 'approved' ? '#388e3c' :
                                   order.items[0].return_details.status === 'rejected' ? '#d32f2f' :
                                   order.items[0].return_details.status === 'processing' ? '#0288d1' :
                                   order.items[0].return_details.status === 'shipping_created' ? '#1565c0' : '#616161'
                          }}>
                            {order.items[0].return_details.status}
                          </span>
                        )}
                      </td>
                      <td>
                        {order.items && order.items[0]?.return_details?.status === 'pending' && (
                          <button
                            className="add-tracking-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleScheduleReturnPickup(order);
                            }}
                            style={{
                              padding: '8px 15px',
                              background: '#000',
                              color: '#fff',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontFamily: 'Helvetica, Arial, sans-serif',
                              letterSpacing: '1px',
                              textTransform: 'uppercase',
                              fontWeight: '400',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            Schedule Return Pickup
                          </button>
                        )}
                        
                        {order.items && (order.items[0]?.return_details?.status === 'shipping_created' || order.items[0]?.return_details?.status === 'completed') && (
                          <button
                            className="view-return-tracking-btn"
                            onClick={(e) => handleViewReturnTrackingInfo(order, e)}
                            disabled={isReturnTrackingLoading}
                            style={{
                              padding: '8px 15px',
                              background: '#fff',
                              color: '#000',
                              border: '1px solid #000',
                              cursor: isReturnTrackingLoading ? 'not-allowed' : 'pointer',
                              fontSize: '11px',
                              fontFamily: 'Helvetica, Arial, sans-serif',
                              letterSpacing: '1px',
                              textTransform: 'uppercase',
                              fontWeight: '400',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {isReturnTrackingLoading ? (
                              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ 
                                  width: '12px', 
                                  height: '12px', 
                                  border: '2px solid rgba(0,0,0,0.3)', 
                                  borderTop: '2px solid #000',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite',
                                  marginRight: '8px',
                                  display: 'inline-block'
                                }}></span>
                                Loading...
                              </span>
                            ) : 'View Return Tracking'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                
                {(!returnOrders.length || !returnOrders.filter(order => order && order.shipping_address).length) && (
                  <tr>
                    <td colSpan="7">
                      <div style={{
                        padding: '60px 0',
                        textAlign: 'center',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                      }}>
                        <div style={{
                          fontSize: '48px',
                          marginBottom: '20px',
                        }}>
                          📦
                        </div>
                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: '400',
                          margin: '0 0 10px 0',
                          letterSpacing: '0.5px',
                        }}>
                          No return orders yet
                        </h3>
                        <p style={{
                          fontSize: '14px',
                          color: '#666',
                          fontWeight: '300',
                          maxWidth: '400px',
                          margin: '0 auto 20px',
                          lineHeight: '1.5',
                        }}>
                          When customers request returns, they'll appear here for processing.
                        </p>
                        <a 
                          href="/products" 
                          style={{
                            padding: '8px 16px',
                            background: '#000',
                            color: '#fff',
                            textDecoration: 'none',
                            fontSize: '11px',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            fontWeight: '400',
                            display: 'inline-block',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          Manage Products
                        </a>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-controls" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '30px 0',
              gap: '10px',
              fontFamily: 'Helvetica, Arial, sans-serif',
            }}>
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 15px',
                  background: currentPage === 1 ? '#f5f5f5' : '#fff',
                  color: currentPage === 1 ? '#ccc' : '#000',
                  border: '1px solid #eee',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '11px',
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  fontWeight: '400',
                  transition: 'all 0.2s ease'
                }}
              >
                Previous
              </button>
              
              <span style={{ fontSize: '14px', color: '#666' }}>
                Page {currentPage} of {totalPages}
              </span>
              
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 15px',
                  background: currentPage === totalPages ? '#f5f5f5' : '#fff',
                  color: currentPage === totalPages ? '#ccc' : '#000',
                  border: '1px solid #eee',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '11px',
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  fontWeight: '400',
                  transition: 'all 0.2s ease'
                }}
              >
                Next
              </button>
            </div>
          )}

          {isModalOpen && selectedOrder && (
            <div className="modal-overlay" onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsModalOpen(false);
              }
            }}>
              <div 
                className="modal-content" 
                style={{ 
                  maxWidth: '95%', 
                  width: '900px', 
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  position: 'relative',
                  padding: '0px',
                  animation: 'slideIn 0.3s ease-out'
                }}
              >
                <div className="modal-header">
                  <h3 className="modal-title">{`Order Details - ${selectedOrder.order_id}`}</h3>
                  <button className="modal-close-btn" onClick={() => setIsModalOpen(false)} aria-label="Close">
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div style={{ padding: '20px' }}>
                    {/* Customer Info and Shipping Address */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                      <div>
                        <h4 style={{ 
                          margin: '0 0 15px 0', 
                          fontSize: '16px', 
                          fontWeight: '500',
                          color: '#333'
                        }}>Customer Information</h4>
                        {selectedOrder.shipping_address ? (
                          <>
                            <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Name: {selectedOrder.shipping_address.name || ''}</p>
                            <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Phone: {selectedOrder.shipping_address.phone || 'N/A'}</p>
                          </>
                        ) : (
                          <p>No customer information available</p>
                        )}
                      </div>

                      {selectedOrder.shipping_address && (
                        <div>
                          <h4 style={{ 
                            margin: '0 0 15px 0', 
                            fontSize: '16px', 
                            fontWeight: '500',
                            color: '#333'
                          }}>Shipping Address</h4>
                          <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>{selectedOrder.shipping_address.name || ''}</p>
                          <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>{selectedOrder.shipping_address.address1 || ''}</p>
                          {selectedOrder.shipping_address.address2 && (
                            <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>{selectedOrder.shipping_address.address2}</p>
                          )}
                          <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>
                            {selectedOrder.shipping_address.city || ''} {selectedOrder.shipping_address.pincode || ''}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Payment Summary */}
                    <div style={{ marginBottom: '30px' }}>
                      <h4 style={{ 
                        margin: '0 0 15px 0', 
                        fontSize: '16px', 
                        fontWeight: '500',
                        color: '#333'
                      }}>Payment Summary</h4>
                      <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Total: {selectedOrder.payment_details?.amount || ''}</p>
                      <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Payment Status: {selectedOrder.payment_details?.status || 'Unknown'}</p>
                      <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Fulfillment Status: {selectedOrder.status || 'Unfulfilled'}</p>
                      {selectedOrder.note && <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Note: {selectedOrder.note}</p>}
                    </div>

                    {/* Order Items */}
                    <div>
                      <h4 style={{ 
                        margin: '0 0 15px 0', 
                        fontSize: '16px', 
                        fontWeight: '500',
                        color: '#333'
                      }}>Order Items</h4>
                      
                      {selectedOrder.items && selectedOrder.items.map((item, index) => (
                        <div key={index} style={{
                          marginBottom: '15px',
                          border: '1px solid #eee',
                          borderRadius: '4px',
                        }}>
                          {/* Basic Item Info */}
                          <div style={{ padding: '15px 20px', display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                            {/* Product Image */}
                            <div style={{ flexShrink: 0, width: '80px', height: '80px' }}>
                              {item.product_images && item.product_images.length > 0 ? (
                                <img 
                                  src={item.product_images[0].src} 
                                  alt={item.name}
                                  style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover',
                                    borderRadius: '4px' 
                                  }}
                                />
                              ) : item.product && item.product.images && item.product.images.length > 0 ? (
                                <img 
                                  src={item.product.images[0].src} 
                                  alt={item.name}
                                  style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover',
                                    borderRadius: '4px' 
                                  }}
                                />
                              ) : (
                                <div style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  backgroundColor: '#f5f5f5', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  borderRadius: '4px',
                                  color: '#aaa',
                                  fontSize: '11px'
                                }}>
                                  No image
                                </div>
                              )}
                            </div>
                            
                            {/* Product Info */}
                            <div style={{ flex: 1 }}>
                              <p style={{ 
                                fontSize: '15px', 
                                fontWeight: '500', 
                                margin: '0 0 10px 0',
                                color: '#333'
                              }}>
                                {item.name}
                              </p>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                                <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                                  Variant: {item.variant_title || 'M'}
                                </p>
                                <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                                  Quantity: {item.quantity || 1}
                                </p>
                                <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                                  Price: ₹{item.price || 1380}
                                </p>
                              </div>
                            </div>
                            
                            {/* Hide Details Button */}
                            <button
                              onClick={() => toggleModalExpandItem(index)}
                              style={{
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontFamily: 'inherit',
                                cursor: 'pointer',
                                marginLeft: 'auto',
                                color: '#555',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#ececec';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                              }}
                            >
                              {modalExpandedItems[index] ? 'HIDE DETAILS' : 'VIEW DETAILS'}
                            </button>
                          </div>
                          
                          {/* Shipping Status */}
                          <div style={{ 
                            borderTop: '1px solid #eee', 
                            padding: '10px 20px',
                            backgroundColor: '#fafafa',
                            fontSize: '13px',
                            color: '#666',
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}>
                            <p style={{ margin: '0' }}>
                              Shipping Status: {item.shipping_info ? 'Shipped' : 'Not Shipped'}
                            </p>
                            
                            {item.delivered_details && (
                              <p style={{ margin: '0', color: '#4CAF50', fontWeight: '500' }}>
                                Delivered on {new Date(item.delivered_details.delivered_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </p>
                            )}
                          </div>
                          
                          {/* Return Request Details - Only visible when expanded */}
                          {modalExpandedItems[index] && item.return_details && (
                            <div style={{ 
                              borderTop: '1px solid #eee',
                              padding: '15px 20px',
                              backgroundColor: '#fff8e1'
                            }}>
                              <h5 style={{ 
                                margin: '0 0 15px 0', 
                                fontSize: '14px', 
                                fontWeight: '600',
                                color: '#333',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                RETURN REQUEST DETAILS
                              </h5>
                              
                              <div style={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '15px',
                                marginBottom: '15px'
                              }}>
                                <div>
                                  <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666', fontWeight: '500', textTransform: 'uppercase' }}>
                                    RETURN ID
                                  </p>
                                  <p style={{ margin: '0', fontSize: '14px' }}>
                                    {item.return_details.return_id}
                                  </p>
                                </div>
                                <div>
                                  <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666', fontWeight: '500', textTransform: 'uppercase' }}>
                                    STATUS
                                  </p>
                                  <p style={{ 
                                    margin: '0', 
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    backgroundColor: item.return_details.status === 'pending' ? '#fff8e1' : '#e8f5e9',
                                    color: item.return_details.status === 'pending' ? '#f57c00' : '#388e3c',
                                    borderRadius: '3px',
                                    fontSize: '12px',
                                    textTransform: 'capitalize',
                                    fontWeight: '500'
                                  }}>
                                    {item.return_details.status}
                                  </p>
                                </div>
                                <div>
                                  <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666', fontWeight: '500', textTransform: 'uppercase' }}>
                                    REQUESTED ON
                                  </p>
                                  <p style={{ margin: '0', fontSize: '14px' }}>
                                    {new Date(item.return_details.created_at).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </p>
                                </div>
                              </div>
                              
                              <div style={{ marginBottom: '15px' }}>
                                <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666', fontWeight: '500', textTransform: 'uppercase' }}>
                                  REASON FOR RETURN
                                </p>
                                <div style={{ 
                                  padding: '10px 15px',
                                  backgroundColor: 'white',
                                  border: '1px solid #eee',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}>
                                  {item.return_details.reason}
                                </div>
                              </div>
                              
                              {item.return_details.description && (
                                <div style={{ marginBottom: '15px' }}>
                                  <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666', fontWeight: '500', textTransform: 'uppercase' }}>
                                    CUSTOMER DESCRIPTION
                                  </p>
                                  <div style={{ 
                                    padding: '10px 15px',
                                    backgroundColor: 'white',
                                    border: '1px solid #eee',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                  }}>
                                    {item.return_details.description}
                                  </div>
                                </div>
                              )}
                              
                              {item.return_details.images && item.return_details.images.length > 0 && (
                                <div>
                                  <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666', fontWeight: '500', textTransform: 'uppercase' }}>
                                    RETURN IMAGES
                                  </p>
                                  <div style={{ 
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '10px'
                                  }}>
                                    {item.return_details.images.map((imageUrl, imgIndex) => (
                                      <div 
                                        key={imgIndex}
                                        style={{
                                          width: '100px',
                                          height: '100px',
                                          border: '1px solid #eee',
                                          borderRadius: '4px',
                                          overflow: 'hidden',
                                          cursor: 'pointer',
                                          position: 'relative',
                                          backgroundColor: 'white'
                                        }}
                                        onClick={() => window.open(imageUrl, '_blank')}
                                      >
                                        <img 
                                          src={imageUrl} 
                                          alt={`Return ${imgIndex + 1}`}
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                          }}
                                        />
                                        <div style={{
                                          position: 'absolute',
                                          bottom: '0',
                                          left: '0',
                                          right: '0',
                                          backgroundColor: 'rgba(0,0,0,0.6)',
                                          color: 'white',
                                          fontSize: '10px',
                                          padding: '2px 5px',
                                          textAlign: 'center'
                                        }}>
                                          Click to enlarge
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isConfirmModalOpen && selectedOrder && (
            <div className="modal-overlay" onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsConfirmModalOpen(false);
              }
            }}>
              <div 
                className="modal-content" 
                style={{ 
                  maxWidth: '90%', 
                  width: '600px', 
                  height: '280px',
                  padding: '0px',
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  position: 'relative',
                  animation: 'slideIn 0.3s ease-out'
                }}
              >
                <div className="modal-header">
                  <h3 className="modal-title">Confirm Schedule Pickup</h3>
                  <button className="modal-close-btn" onClick={() => setIsConfirmModalOpen(false)} aria-label="Close">
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <p style={{ 
                    marginBottom: '15px',
                    fontSize: '16px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: '300'
                  }}>
                    Are you sure you want to schedule pickup for order #{selectedOrder.order_id}?
                  </p>
                  {apiError && (
                    <div style={{
                      color: '#d32f2f',
                      marginBottom: '15px',
                      padding: '8px',
                      backgroundColor: '#fff3f3',
                      borderRadius: '4px'
                    }}>
                      {apiError}
                    </div>
                  )}
                  <div style={{ 
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'flex-end',
                    marginTop: '20px'
                  }}>
                    <button 
                      type="button"
                      onClick={() => setIsConfirmModalOpen(false)}
                      disabled={isConfirmLoading}
                      style={{
                        padding: '8px 15px',
                        background: '#fff',
                        color: '#000',
                        border: '1px solid #000',
                        cursor: isConfirmLoading ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        fontWeight: '400',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button"
                      onClick={handleConfirmSchedule}
                      disabled={isConfirmLoading}
                      style={{
                        padding: '8px 15px',
                        background: '#000',
                        color: '#fff',
                        border: 'none',
                        cursor: isConfirmLoading ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        fontWeight: '400',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        minWidth: '150px'
                      }}
                    >
                      {isConfirmLoading ? (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ 
                            width: '12px', 
                            height: '12px', 
                            border: '2px solid rgba(255,255,255,0.3)', 
                            borderTop: '2px solid #fff',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            marginRight: '8px',
                            display: 'inline-block'
                          }}></span>
                          Processing...
                        </span>
                      ) : 'Confirm Schedule'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isTrackingModalOpen && selectedOrder && (
            <div className="modal-overlay" onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsTrackingModalOpen(false);
              }
            }}>
              <div 
                className="modal-content" 
                style={{ 
                  maxWidth: '90%', 
                  width: '800px', 
                  height: '80vh',
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  padding: '0px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  position: 'relative',
                  animation: 'slideIn 0.3s ease-out'
                }}
              >
                <div className="modal-header">
                  <h3 className="modal-title">TRACKING INFORMATION</h3>
                  <button className="modal-close-btn" onClick={() => setIsTrackingModalOpen(false)} aria-label="Close">
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div style={{ marginBottom: '40px' }}>
                    <p style={{ 
                      margin: '0 0 15px 0', 
                      fontWeight: '500', 
                      textTransform: 'uppercase', 
                      letterSpacing: '1px', 
                      fontSize: '11px',
                      borderBottom: '1px solid #eee',
                      paddingBottom: '10px'
                    }}>
                      ORDER ITEMS
                    </p>
                    
                    {selectedOrder.items && selectedOrder.items.map((item, index) => (
                      <div 
                        key={index} 
                        style={{ 
                          padding: '15px 0',
                          borderBottom: '1px solid #f5f5f5',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <p style={{ 
                            margin: '0 0 5px 0', 
                            fontWeight: '500',
                            fontSize: '14px'
                          }}>
                            {item.name}
                          </p>
                          <p style={{ 
                            margin: '0',
                            fontSize: '13px',
                            color: '#666',
                            fontWeight: '300'
                          }}>
                            Variant: {item.variant_title || 'Default'} • Qty: {item.quantity}
                          </p>
                          {item.shipping_info?.tracking_info?.awb_code && (
                            <p style={{ 
                              margin: '5px 0 0 0',
                              fontSize: '13px',
                              fontWeight: '300'
                            }}>
                              Tracking: {item.shipping_info.tracking_info.awb_code}
                            </p>
                          )}
                          {item.delivered_details && (
                            <p style={{ 
                              margin: '5px 0 0 0',
                              fontSize: '13px',
                              fontWeight: '500',
                              color: '#4CAF50'
                            }}>
                              Delivered on {new Date(item.delivered_details.delivered_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          )}
                        </div>
                        
                        <div style={{ display: 'flex', gap: '10px' }}>
                          {item.shipping_info && (
                            <>
                              {item.shipping_info.shipping_documents ? (
                                <div style={{ 
                                  display: 'flex', 
                                  gap: '15px',
                                  alignItems: 'center'
                                }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadDocument(item.shipping_info.shipping_documents.manifest_url, `manifest-${item.shipping_info.shiprocket_shipment_id}.pdf`);
                                    }}
                                    style={{
                                      padding: '0',
                                      background: 'none',
                                      border: 'none',
                                      color: '#000',
                                      cursor: 'pointer',
                                      fontSize: '11px',
                                      fontFamily: 'Helvetica, Arial, sans-serif',
                                      letterSpacing: '1px',
                                      textTransform: 'uppercase',
                                      fontWeight: '400',
                                      textDecoration: 'underline'
                                    }}
                                  >
                                    Manifest
                                  </button>
                                  <span style={{ color: '#666' }}>•</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadDocument(item.shipping_info.shipping_documents.label_url, `label-${item.shipping_info.shiprocket_shipment_id}.pdf`);
                                    }}
                                    style={{
                                      padding: '0',
                                      background: 'none',
                                      border: 'none',
                                      color: '#000',
                                      cursor: 'pointer',
                                      fontSize: '11px',
                                      fontFamily: 'Helvetica, Arial, sans-serif',
                                      letterSpacing: '1px',
                                      textTransform: 'uppercase',
                                      fontWeight: '400',
                                      textDecoration: 'underline'
                                    }}
                                  >
                                    Label
                                  </button>
                                  <span style={{ color: '#666' }}>•</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadDocument(item.shipping_info.shipping_documents.invoice_url, `invoice-${item.shipping_info.shiprocket_shipment_id}.pdf`);
                                    }}
                                    style={{
                                      padding: '0',
                                      background: 'none',
                                      border: 'none',
                                      color: '#000',
                                      cursor: 'pointer',
                                      fontSize: '11px',
                                      fontFamily: 'Helvetica, Arial, sans-serif',
                                      letterSpacing: '1px',
                                      textTransform: 'uppercase',
                                      fontWeight: '400',
                                      textDecoration: 'underline'
                                    }}
                                  >
                                    Invoice
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    fetchShippingDocuments(selectedOrder.order_id, item.shipping_info.shiprocket_order_id, item.shipping_info.shiprocket_shipment_id);
                                  }}
                                  disabled={isDocumentLoading[item.shipping_info.shiprocket_shipment_id]}
                                  style={{
                                    padding: '8px 15px',
                                    background: '#fff',
                                    color: '#000',
                                    border: '1px solid #000',
                                    cursor: isDocumentLoading[item.shipping_info.shiprocket_shipment_id] ? 'not-allowed' : 'pointer',
                                    fontSize: '11px',
                                    fontFamily: 'Helvetica, Arial, sans-serif',
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    fontWeight: '400',
                                    transition: 'all 0.2s ease',
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}
                                >
                                  {isDocumentLoading[item.shipping_info.shiprocket_shipment_id] ? (
                                    <>
                                      <span style={{ 
                                        width: '12px', 
                                        height: '12px', 
                                        border: '2px solid rgba(0,0,0,0.3)', 
                                        borderTop: '2px solid #000',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        display: 'inline-block'
                                      }}></span>
                                      Loading...
                                    </>
                                  ) : 'Shipping Documents'}
                                </button>
                              )}
                              
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  selectItemForTracking(item);
                                }}
                                style={{
                                  padding: '8px 15px',
                                  background: selectedItemForTracking === item ? '#000' : '#fff',
                                  color: selectedItemForTracking === item ? '#fff' : '#000',
                                  border: '1px solid #000',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontFamily: 'Helvetica, Arial, sans-serif',
                                  letterSpacing: '1px',
                                  textTransform: 'uppercase',
                                  fontWeight: '400',
                                  transition: 'all 0.2s ease',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {selectedItemForTracking === item ? 'SELECTED' : 'VIEW TRACKING'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {selectedItemForTracking && selectedTrackingInfo && (
                    <>
                      <div style={{ marginBottom: '40px' }}>
                        <p style={{ 
                          margin: '0 0 15px 0', 
                          fontWeight: '500', 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px', 
                          fontSize: '11px',
                          borderBottom: '1px solid #eee',
                          paddingBottom: '10px'
                        }}>
                          SHIPMENT DETAILS
                        </p>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1fr 1fr', 
                          gap: '30px',
                          fontSize: '14px',
                          lineHeight: '1.5'
                        }}>
                          <div>
                            {selectedTrackingInfo.tracking_info?.awb_code && (
                              <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '11px' }}>
                                  AWB/TRACKING NUMBER
                                </p>
                                <p style={{ margin: '0', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.awb_code}
                                </p>
                              </div>
                            )}
                            
                            {selectedTrackingInfo.shiprocket_order_id && (
                              <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '11px' }}>
                                  ORDER ID
                                </p>
                                <p style={{ margin: '0', fontWeight: '300' }}>
                                  {selectedTrackingInfo.shiprocket_order_id}
                                </p>
                              </div>
                            )}
                            
                            {selectedTrackingInfo.warehouse_id && (
                              <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '11px' }}>
                                  WAREHOUSE ID
                                </p>
                                <p style={{ margin: '0', fontWeight: '300' }}>
                                  {selectedTrackingInfo.warehouse_id}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          <div>
                            {selectedTrackingInfo.tracking_info?.courier_name && (
                              <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '11px' }}>
                                  COURIER
                                </p>
                                <p style={{ margin: '0', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.courier_name}
                                </p>
                              </div>
                            )}
                            
                            {selectedTrackingInfo.tracking_info?.invoice_no && (
                              <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '11px' }}>
                                  INVOICE
                                </p>
                                <p style={{ margin: '0', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.invoice_no}
                                </p>
                              </div>
                            )}
                            
                            {selectedTrackingInfo.tracking_info?.applied_weight && (
                              <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '11px' }}>
                                  WEIGHT
                                </p>
                                <p style={{ margin: '0', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.applied_weight} kg
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {selectedTrackingInfo.tracking_info?.pickup_details && (
                        <div style={{ marginBottom: '40px' }}>
                          <p style={{ 
                            margin: '0 0 15px 0', 
                            fontWeight: '500', 
                            textTransform: 'uppercase', 
                            letterSpacing: '1px', 
                            fontSize: '11px',
                            borderBottom: '1px solid #eee',
                            paddingBottom: '10px'
                          }}>
                            PICKUP DETAILS
                          </p>
                          
                          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                            {selectedTrackingInfo.tracking_info.pickup_details.response?.pickup_token_number && (
                              <div style={{ display: 'flex', marginBottom: '5px' }}>
                                <p style={{ 
                                  margin: '0', 
                                  fontWeight: '500', 
                                  width: '150px',
                                  fontSize: '13px'
                                }}>Reference:</p>
                                <p style={{ margin: '0', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.pickup_details.response.pickup_token_number}
                                </p>
                              </div>
                            )}
                            
                            {selectedTrackingInfo.tracking_info.pickup_details.response?.pickup_scheduled_date && (
                              <div style={{ display: 'flex', marginBottom: '5px' }}>
                                <p style={{ 
                                  margin: '0', 
                                  fontWeight: '500', 
                                  width: '150px',
                                  fontSize: '13px'
                                }}>Scheduled Date:</p>
                                <p style={{ margin: '0', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.pickup_details.response.pickup_scheduled_date}
                                </p>
                              </div>
                            )}
                            
                            {selectedTrackingInfo.tracking_info.pickup_details.response?.data && (
                              <div style={{ display: 'flex', marginBottom: '5px' }}>
                                <p style={{ 
                                  margin: '0', 
                                  fontWeight: '500', 
                                  width: '150px',
                                  fontSize: '13px'
                                }}>Status:</p>
                                <p style={{ margin: '0', fontWeight: '300' }}>
                                  {selectedTrackingInfo.tracking_info.pickup_details.response.data}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {selectedTrackingInfo.tracking_updates?.tracking_data && (
                        <div>
                          {/* Current Status Section */}
                          {selectedTrackingInfo.tracking_updates.tracking_data.shipment_track?.map((track, index) => (
                            <div key={index} style={{ marginBottom: '40px' }}>
                              <p style={{ 
                                margin: '0 0 15px 0', 
                                fontWeight: '500', 
                                textTransform: 'uppercase', 
                                letterSpacing: '1px', 
                                fontSize: '11px',
                                borderBottom: '1px solid #eee',
                                paddingBottom: '10px'
                              }}>
                                CURRENT STATUS
                              </p>
                              
                              <div style={{ 
                                display: 'flex',
                                justifyContent: 'space-between', 
                                fontSize: '15px',
                                marginBottom: '20px' 
                              }}>
                                <p style={{ 
                                  fontWeight: '600', 
                                  margin: '0',
                                  textTransform: 'uppercase',
                                  fontSize: '16px'
                                }}>
                                  {track.current_status}
                                </p>
                                
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'auto auto', 
                                    gap: '20px',
                                    fontSize: '14px'
                                  }}>
                                    <div>
                                      <p style={{ margin: '0 0 5px 0', fontWeight: '500' }}>Origin</p>
                                      <p style={{ margin: '0', fontWeight: '300' }}>{track.origin}</p>
                                    </div>
                                    <div>
                                      <p style={{ margin: '0 0 5px 0', fontWeight: '500' }}>Destination</p>
                                      <p style={{ margin: '0', fontWeight: '300' }}>{track.destination}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                                gap: '20px',
                                fontSize: '14px'
                              }}>
                                {track.edd && (
                                  <div>
                                    <p style={{ 
                                      margin: '0 0 5px 0', 
                                      fontWeight: '500', 
                                      textTransform: 'uppercase', 
                                      letterSpacing: '1px', 
                                      fontSize: '11px' 
                                    }}>
                                      Expected Delivery
                                    </p>
                                    <p style={{ margin: '0', fontWeight: '300' }}>{track.edd}</p>
                                  </div>
                                )}
                                
                                {track.consignee_name && (
                                  <div>
                                    <p style={{ 
                                      margin: '0 0 5px 0', 
                                      fontWeight: '500', 
                                      textTransform: 'uppercase', 
                                      letterSpacing: '1px', 
                                      fontSize: '11px' 
                                    }}>
                                      Recipient
                                    </p>
                                    <p style={{ margin: '0', fontWeight: '300' }}>{track.consignee_name}</p>
                                  </div>
                                )}
                                
                                {track.pickup_date && (
                                  <div>
                                    <p style={{ 
                                      margin: '0 0 5px 0', 
                                      fontWeight: '500', 
                                      textTransform: 'uppercase', 
                                      letterSpacing: '1px', 
                                      fontSize: '11px' 
                                    }}>
                                      Pickup Date
                                    </p>
                                    <p style={{ margin: '0', fontWeight: '300' }}>{track.pickup_date}</p>
                                  </div>
                                )}
                                
                                {track.delivered_date && (
                                  <div>
                                    <p style={{ 
                                      margin: '0 0 5px 0', 
                                      fontWeight: '500', 
                                      textTransform: 'uppercase', 
                                      letterSpacing: '1px', 
                                      fontSize: '11px' 
                                    }}>
                                      Delivered Date
                                    </p>
                                    <p style={{ margin: '0', fontWeight: '300' }}>{track.delivered_date}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}

                          {/* Tracking Timeline */}
                          {selectedTrackingInfo.tracking_updates.tracking_data.shipment_track_activities && 
                           selectedTrackingInfo.tracking_updates.tracking_data.shipment_track_activities.length > 0 && (
                            <div>
                              <p style={{ 
                                margin: '0 0 25px 0', 
                                fontWeight: '500', 
                                textTransform: 'uppercase', 
                                letterSpacing: '1px', 
                                fontSize: '11px',
                                borderBottom: '1px solid #eee',
                                paddingBottom: '10px'
                              }}>
                                TRACKING HISTORY
                              </p>
                              
                              <div style={{ paddingLeft: '10px' }}>
                                {selectedTrackingInfo.tracking_updates.tracking_data.shipment_track_activities.map((activity, index) => {
                                  // Convert date to more readable format
                                  const activityDate = new Date(activity.date);
                                  const formattedDate = activityDate.toLocaleDateString('en-US', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  });
                                  
                                  const formattedTime = activityDate.toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  });
                                  
                                  const isFirst = index === 0;
                                  const isLast = index === selectedTrackingInfo.tracking_updates.tracking_data.shipment_track_activities.length - 1;
                                  
                                  return (
                                    <div key={index} style={{ 
                                      position: 'relative',
                                      paddingLeft: '30px',
                                      marginBottom: '30px',
                                      paddingBottom: isLast ? '0' : '30px',
                                      borderLeft: isLast ? 'none' : '1px solid #eee'
                                    }}>
                                      {/* Circle marker */}
                                      <div style={{
                                        position: 'absolute',
                                        left: '-5px',
                                        top: '0',
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        backgroundColor: isFirst ? '#000' : '#eee',
                                        border: 'none'
                                      }}></div>
                                      
                                      <div style={{ marginBottom: '5px' }}>
                                        <p style={{ 
                                          margin: '0', 
                                          fontWeight: '500',
                                          fontSize: '14px',
                                          textTransform: isFirst ? 'uppercase' : 'none'
                                        }}>
                                          {activity.activity}
                                        </p>
                                        
                                        <div style={{ 
                                          display: 'flex',
                                          marginTop: '5px',
                                          fontSize: '13px',
                                          color: '#666',
                                          fontWeight: '300'
                                        }}>
                                          <p style={{ margin: '0 15px 0 0' }}>{formattedDate}</p>
                                          <p style={{ margin: '0' }}>{formattedTime}</p>
                                        </div>
                                      </div>
                                      
                                      {activity.location && (
                                        <p style={{ 
                                          margin: '10px 0 0 0',
                                          fontSize: '13px',
                                          color: '#666',
                                          fontWeight: '300'
                                        }}>
                                          {activity.location}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {selectedTrackingInfo.tracking_updates.tracking_data.track_url && (
                            <div style={{ marginTop: '40px', textAlign: 'center' }}>
                              <a 
                                href={selectedTrackingInfo.tracking_updates.tracking_data.track_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{
                                  backgroundColor: '#000',
                                  color: '#fff',
                                  textDecoration: 'none',
                                  padding: '12px 40px',
                                  fontSize: '13px',
                                  letterSpacing: '1px',
                                  textTransform: 'uppercase',
                                  border: 'none',
                                  fontWeight: '400',
                                  cursor: 'pointer',
                                  display: 'inline-block'
                                }}
                              >
                                Track on Carrier Website
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  
                  {!selectedItemForTracking && (
                    <div style={{ 
                      padding: '30px 0',
                      textAlign: 'center',
                      color: '#666',
                      fontWeight: '300',
                      fontSize: '14px'
                    }}>
                      Select an item to view its tracking information
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {isReturnConfirmModalOpen && selectedOrder && (
            <div className="modal-overlay" onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsReturnConfirmModalOpen(false);
              }
            }}>
              <div 
                className="modal-content" 
                style={{ 
                  maxWidth: '90%', 
                  width: '600px', 
                  height: 'auto',
                  maxHeight: '90vh',
                  padding: '0px',
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  position: 'relative',
                  animation: 'slideIn 0.3s ease-out'
                }}
              >
                <div className="modal-header">
                  <h3 className="modal-title">Schedule Return Pickup</h3>
                  <button className="modal-close-btn" onClick={() => setIsReturnConfirmModalOpen(false)} aria-label="Close">
                    ×
                  </button>
                </div>
                <div className="modal-body" style={{ padding: '20px' }}>
                  <p style={{ 
                    marginBottom: '20px',
                    fontSize: '16px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: '300'
                  }}>
                    Schedule pickup for return #{selectedOrder.items[0]?.return_details?.return_id}
                  </p>
                  
                  {/* Warehouse Selection */}
                  <div style={{ marginBottom: '25px' }}>
                    <h4 style={{ 
                      margin: '0 0 15px 0', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#333',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      SELECT RETURN LOCATION
                    </h4>
                    
                    {isLoadingLocations ? (
                      <div style={{ 
                        padding: '15px', 
                        textAlign: 'center', 
                        backgroundColor: '#f9f9f9',
                        borderRadius: '4px'
                      }}>
                        <span style={{ 
                          width: '20px', 
                          height: '20px', 
                          border: '2px solid rgba(0,0,0,0.1)', 
                          borderTop: '2px solid #000',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          display: 'inline-block',
                          marginRight: '10px',
                          verticalAlign: 'middle'
                        }}></span>
                        <span style={{ verticalAlign: 'middle', fontSize: '14px' }}>Loading warehouses...</span>
                      </div>
                    ) : returnLocations.length > 0 ? (
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {returnLocations.map((warehouse, index) => (
                          <div 
                            key={warehouse._id}
                            onClick={() => setSelectedWarehouse(warehouse)}
                            style={{
                              padding: '15px',
                              marginBottom: '10px',
                              border: `1px solid ${selectedWarehouse && selectedWarehouse._id === warehouse._id ? '#000' : '#eee'}`,
                              borderRadius: '4px',
                              backgroundColor: selectedWarehouse && selectedWarehouse._id === warehouse._id ? '#f9f9f9' : '#fff',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              position: 'relative'
                            }}
                          >
                            {/* Selection indicator */}
                            {selectedWarehouse && selectedWarehouse._id === warehouse._id && (
                              <div style={{
                                position: 'absolute',
                                top: '15px',
                                right: '15px',
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#000',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px'
                              }}>
                                ✓
                              </div>
                            )}
                            
                            <h5 style={{ 
                              margin: '0 0 8px 0', 
                              fontWeight: '500',
                              fontSize: '15px'
                            }}>
                              {warehouse.name}
                            </h5>
                            
                            <p style={{ 
                              margin: '0 0 5px 0',
                              fontSize: '13px',
                              color: '#666',
                              fontWeight: '300'
                            }}>
                              {warehouse.address}
                            </p>
                            
                            <div style={{ 
                              fontSize: '12px',
                              color: '#777',
                              marginTop: '8px',
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '15px'
                            }}>
                              <span>Operating Hours: {warehouse.operationHoursStart.substring(0, 2)}:{warehouse.operationHoursStart.substring(2)} - {warehouse.operationHoursEnd.substring(0, 2)}:{warehouse.operationHoursEnd.substring(2)}</span>
                              <span>Contact: {warehouse.pocName}, {warehouse.pocPhone}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        backgroundColor: '#f9f9f9',
                        borderRadius: '4px',
                        color: '#666'
                      }}>
                        No return locations available
                      </div>
                    )}
                  </div>
                  
                  {apiError && (
                    <div style={{
                      color: '#d32f2f',
                      marginBottom: '15px',
                      padding: '8px',
                      backgroundColor: '#fff3f3',
                      borderRadius: '4px'
                    }}>
                      {apiError}
                    </div>
                  )}
                  
                  <div style={{ 
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'flex-end',
                    marginTop: '20px'
                  }}>
                    <button 
                      type="button"
                      onClick={() => setIsReturnConfirmModalOpen(false)}
                      disabled={isConfirmLoading}
                      style={{
                        padding: '8px 15px',
                        background: '#fff',
                        color: '#000',
                        border: '1px solid #000',
                        cursor: isConfirmLoading ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        fontWeight: '400',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button"
                      onClick={handleConfirmReturnPickup}
                      disabled={isConfirmLoading || !selectedWarehouse}
                      style={{
                        padding: '8px 15px',
                        background: selectedWarehouse ? '#000' : '#e0e0e0',
                        color: '#fff',
                        border: 'none',
                        cursor: isConfirmLoading || !selectedWarehouse ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        fontWeight: '400',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        minWidth: '150px'
                      }}
                    >
                      {isConfirmLoading ? (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ 
                            width: '12px', 
                            height: '12px', 
                            border: '2px solid rgba(255,255,255,0.3)', 
                            borderTop: '2px solid #fff',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            marginRight: '8px',
                            display: 'inline-block'
                          }}></span>
                          Processing...
                        </span>
                      ) : 'Schedule Pickup'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add Return Tracking Modal */}
          {isReturnTrackingModalOpen && selectedOrder && returnTrackingInfo && (
            <div className="modal-overlay" onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsReturnTrackingModalOpen(false);
              }
            }}>
              <div 
                className="modal-content" 
                style={{ 
                  maxWidth: '90%', 
                  width: '800px', 
                  height: 'auto',
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  padding: '0px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  position: 'relative',
                  animation: 'slideIn 0.3s ease-out'
                }}
              >
                <div className="modal-header">
                  <h3 className="modal-title">RETURN TRACKING INFORMATION</h3>
                  <button 
                    className="modal-close-btn" 
                    onClick={() => setIsReturnTrackingModalOpen(false)} 
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div style={{ padding: '20px' }}>
                    {/* Return Tracking Info Summary */}
                    <div style={{ marginBottom: '30px' }}>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                        gap: '20px',
                        marginBottom: '20px' 
                      }}>
                        <div>
                          <p style={{ 
                            margin: '0 0 5px 0', 
                            fontWeight: '500', 
                            textTransform: 'uppercase', 
                            letterSpacing: '1px', 
                            fontSize: '11px' 
                          }}>
                            Return ID
                          </p>
                          <p style={{ margin: '0', fontSize: '14px', fontWeight: '300' }}>
                            {returnTrackingInfo.return_id}
                          </p>
                        </div>
                        
                        <div>
                          <p style={{ 
                            margin: '0 0 5px 0', 
                            fontWeight: '500', 
                            textTransform: 'uppercase', 
                            letterSpacing: '1px', 
                            fontSize: '11px' 
                          }}>
                            Order ID
                          </p>
                          <p style={{ margin: '0', fontSize: '14px', fontWeight: '300' }}>
                            {returnTrackingInfo.order_id}
                          </p>
                        </div>
                        
                        <div>
                          <p style={{ 
                            margin: '0 0 5px 0', 
                            fontWeight: '500', 
                            textTransform: 'uppercase', 
                            letterSpacing: '1px', 
                            fontSize: '11px' 
                          }}>
                            AWB Code
                          </p>
                          <p style={{ margin: '0', fontSize: '14px', fontWeight: '300' }}>
                            {returnTrackingInfo.awb_code}
                          </p>
                        </div>
                      </div>
                      
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: '15px',
                        padding: '15px',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '4px'
                      }}>
                        <div>
                          <p style={{ 
                            margin: '0 0 5px 0', 
                            fontWeight: '500', 
                            fontSize: '15px'
                          }}>
                            Current Status
                          </p>
                          <p style={{ 
                            margin: '0', 
                            fontSize: '16px', 
                            fontWeight: '600',
                            color: '#1565c0'
                          }}>
                            {returnTrackingInfo.current_status}
                          </p>
                        </div>
                        
                        <a 
                          href={returnTrackingInfo.tracking_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{
                            backgroundColor: '#000',
                            color: '#fff',
                            textDecoration: 'none',
                            padding: '10px 20px',
                            fontSize: '11px',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            border: 'none',
                            fontWeight: '400',
                            cursor: 'pointer',
                            borderRadius: '4px'
                          }}
                        >
                          Track on Carrier Website
                        </a>
                      </div>
                    </div>

                    {/* Timeline of tracking activities */}
                    {returnTrackingInfo.tracking_info && returnTrackingInfo.tracking_info.activities && 
                     returnTrackingInfo.tracking_info.activities.length > 0 && (
                      <div>
                        <h4 style={{ 
                          margin: '0 0 20px 0', 
                          fontWeight: '500', 
                          fontSize: '16px',
                          borderBottom: '1px solid #eee',
                          paddingBottom: '10px'
                        }}>
                          Tracking History
                        </h4>
                        
                        <div style={{ paddingLeft: '10px' }}>
                          {returnTrackingInfo.tracking_info.activities.map((activity, index) => {
                            // Convert date to more readable format
                            const activityDate = new Date(activity.date);
                            const formattedDate = activityDate.toLocaleDateString('en-US', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            });
                            
                            const formattedTime = activityDate.toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            });
                            
                            const isFirst = index === 0;
                            const isLast = index === returnTrackingInfo.tracking_info.activities.length - 1;
                            
                            return (
                              <div key={index} style={{ 
                                position: 'relative',
                                paddingLeft: '30px',
                                marginBottom: '30px',
                                paddingBottom: isLast ? '0' : '30px',
                                borderLeft: isLast ? 'none' : '1px solid #eee'
                              }}>
                                {/* Circle marker */}
                                <div style={{
                                  position: 'absolute',
                                  left: '-5px',
                                  top: '0',
                                  width: '10px',
                                  height: '10px',
                                  borderRadius: '50%',
                                  backgroundColor: isFirst ? '#1565c0' : '#eee',
                                  border: 'none'
                                }}></div>
                                
                                <div style={{ marginBottom: '5px' }}>
                                  <p style={{ 
                                    margin: '0', 
                                    fontWeight: '500',
                                    fontSize: '14px',
                                    textTransform: isFirst ? 'uppercase' : 'none'
                                  }}>
                                    {activity.activity || activity['sr-status-label'] || activity.status}
                                  </p>
                                  
                                  <div style={{ 
                                    display: 'flex',
                                    marginTop: '5px',
                                    fontSize: '13px',
                                    color: '#666',
                                    fontWeight: '300'
                                  }}>
                                    <p style={{ margin: '0 15px 0 0' }}>{formattedDate}</p>
                                    <p style={{ margin: '0' }}>{formattedTime}</p>
                                  </div>
                                </div>
                                
                                {activity.location && (
                                  <p style={{ 
                                    margin: '10px 0 0 0',
                                    fontSize: '13px',
                                    color: '#666',
                                    fontWeight: '300'
                                  }}>
                                    {activity.location}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Display raw tracking data if available */}
                    {returnTrackingInfo.tracking_info?.raw_tracking_data?.shipment_track?.length > 0 && (
                      <div style={{ marginTop: '30px' }}>
                        <h4 style={{ 
                          margin: '0 0 20px 0', 
                          fontWeight: '500', 
                          fontSize: '16px',
                          borderBottom: '1px solid #eee',
                          paddingBottom: '10px'
                        }}>
                          Shipment Details
                        </h4>
                        
                        {returnTrackingInfo.tracking_info.raw_tracking_data.shipment_track.map((track, index) => (
                          <div key={index} style={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                            gap: '15px',
                            marginBottom: '15px',
                            padding: '15px',
                            backgroundColor: '#f9f9f9',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}>
                            {track.courier_name && (
                              <div>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', fontSize: '12px', color: '#666' }}>
                                  Courier
                                </p>
                                <p style={{ margin: '0', fontWeight: '400' }}>{track.courier_name}</p>
                              </div>
                            )}
                            
                            {track.consignee_name && (
                              <div>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', fontSize: '12px', color: '#666' }}>
                                  Consignee
                                </p>
                                <p style={{ margin: '0', fontWeight: '400' }}>{track.consignee_name}</p>
                              </div>
                            )}
                            
                            {track.origin && (
                              <div>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', fontSize: '12px', color: '#666' }}>
                                  Origin
                                </p>
                                <p style={{ margin: '0', fontWeight: '400' }}>{track.origin}</p>
                              </div>
                            )}
                            
                            {track.destination && (
                              <div>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', fontSize: '12px', color: '#666' }}>
                                  Destination
                                </p>
                                <p style={{ margin: '0', fontWeight: '400' }}>{track.destination}</p>
                              </div>
                            )}
                            
                            {track.weight && (
                              <div>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', fontSize: '12px', color: '#666' }}>
                                  Weight
                                </p>
                                <p style={{ margin: '0', fontWeight: '400' }}>{track.weight} kg</p>
                              </div>
                            )}
                            
                            {track.packages && (
                              <div>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', fontSize: '12px', color: '#666' }}>
                                  Packages
                                </p>
                                <p style={{ margin: '0', fontWeight: '400' }}>{track.packages}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Returns; 